import React, { useState, useEffect, useCallback } from "react";
import { Edit, Eye } from "lucide-react";
import { Table, Button, Tag, message, Select, Input, DatePicker, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getRequest } from "../../../../service";
import LeaveRequest from "../../../../modal/admin/leaveRequestModal";
import ModalWrapper from "../../../../Modal";
import { getEmployeeData } from "../../../../utils/localStorage";
import { formatDate } from "../../../../utils/dateTime";

const { RangePicker } = DatePicker;

const Leave = () => {
  const userData = getEmployeeData();
  const role = userData?.role;
  
  // Permission checks
  const leaveRequestPermissions = userData?.designation?.permissions?.Leaves?.['Leaves Request'] || [];
  
  const canView = role === "companyadmin" || leaveRequestPermissions.includes("view");
  const canEdit = role === "companyadmin" || leaveRequestPermissions.includes("edit");

  const [searchText, setSearchText] = useState("");
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Status filter options
  const statusOptions = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "cancelled", label: "Cancelled" }
  ];

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchTerm) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setPagination(prev => ({ ...prev, current: 1 }));
          fetchData(1, pagination.pageSize, searchTerm, statusFilter, dateRange);
        }, 500); // 500ms delay
      };
    })(),
    [pagination.pageSize, statusFilter, dateRange]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData(1, pagination.pageSize, searchText, value, dateRange);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData(1, pagination.pageSize, searchText, statusFilter, dates);
  };

  useEffect(() => {
    fetchData();
  }, [canView]);

  const fetchData = async (page = 1, pageSize = 10, searchTerm = searchText, statusTerm = statusFilter, dateRangeTerm = dateRange) => {
    setLoading(true);
    try {
      if (!canView) {
        setLeaveRequests([]);
        setEmployees([]);
        setPagination(prev => ({ ...prev, total: 0 }));
        return;
      }
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      });

      // Add search parameter if provided
      if (searchTerm && searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      // Add status parameter if not "all"
      if (statusTerm && statusTerm !== "all") {
        queryParams.append('status', statusTerm);
      }

      // Add date range parameters if provided
      if (dateRangeTerm && dateRangeTerm.length === 2) {
        const startDate = dateRangeTerm[0].format('YYYY-MM-DD');
        const endDate = dateRangeTerm[1].format('YYYY-MM-DD');
        queryParams.append('startDate', startDate);
        queryParams.append('endDate', endDate);
      }
      
      // Fetch leave requests
      let leaveRes;
      if(role === "companyadmin"){
        leaveRes = await getRequest(`/api/leaveRequest?${queryParams.toString()}`);
      }else{
        queryParams.append('employeeId', userData?.userId);
        queryParams.append('onlyMe', 'false');
        leaveRes = await getRequest(`/api/leaveRequest?${queryParams.toString()}`);
      }
      const empRes = await getRequest("/api/employees");
      
      const requestData = leaveRes?.leaveRequests || [];
      
      // Handle pagination from API response
      const apiPagination = leaveRes?.pagination || {};
      const totalCount = apiPagination.totalItems || 0;
      const currentPage = apiPagination.currentPage || page;
      const itemsPerPage = apiPagination.itemsPerPage || pageSize;

      setLeaveRequests(requestData);
      setEmployees(empRes?.employees || []);
      setPagination(prev => ({
        ...prev,
        current: currentPage,
        pageSize: itemsPerPage,
        total: totalCount
      }));
    } catch (error) {
      message.error("Failed to fetch leave requests or employees");
      setLeaveRequests([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };
  // Helper to get employee name from employeeId
  const getEmployeeName = (id) => {
    const emp = employees.find((e) => e.id === id || e._id === id); 
    return emp ? `${emp.firstName} ${emp.lastName}` : id;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleEdit = (row) => {
    if (!canEdit) {
      message.warning("You don't have permission to edit leave requests.");
      return;
    }
    setEditData(row.raw);
    setEditModalOpen(true);
  };
  const handleView = (row) => {
    setViewData(row.raw);
    setViewModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditData(null);
    fetchData(pagination.current, pagination.pageSize, searchText, statusFilter, dateRange);
  };
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewData(null);
  };

  const handleTableChange = (paginationInfo, filters, sorter) => {
    // Update pagination state
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current || 1,
      pageSize: paginationInfo.pageSize || 10
    }));
    
    // Fetch data with new pagination
    fetchData(
      paginationInfo.current || 1, 
      paginationInfo.pageSize || 10, 
      searchText, 
      statusFilter, 
      dateRange
    );
  };

  // Transform API data for the table
  const tableData = leaveRequests.map((item, idx) => {
    return {
      key: item._id,
      id: idx + 1,
      employeeName: getEmployeeName(item.employeeId),
      date: item.createdAt ? formatDate(item.createdAt) : '',
      flagType: item.leaveTypeName || '',
      status: item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : '',
      approvals: (item.approvedByInfo && item.approvedByInfo.name) ? item.approvedByInfo.name : (item.approvedBy || '-'),
      addOn: item.files && item.files.length > 0 ? 'Yes' : 'No',
      numDays: item.leaveDays,
      raw: item,
    };
  });

  const requestColumns = [
    {
      title: "Sr",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Flag Type",
      dataIndex: "flagType",
      key: "flagType",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Approvals",
      dataIndex: "approvals",
      key: "approvals",
    },
    {
      title: "No. of Days",
      dataIndex: "numDays",
      key: "numDays",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isApproved = record.status.toLowerCase() === 'approved';
        const canEditRecord = canEdit && !isApproved;
        
        return (
          <>
            <Button 
              type="text" 
              icon={<Edit className="w-4 h-4" />} 
              onClick={() => handleEdit(record)}
              className={canEditRecord ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
              disabled={!canEditRecord}
              title={!canEdit ? "You don't have permission to edit" : isApproved ? "Cannot edit approved requests" : "Edit"}
            />
            <Button 
              type="text" 
              icon={<Eye className="w-4 h-4 text-blue-600" />} 
              onClick={() => handleView(record)}
              title="View"
            />
          </>
        );
      },
    },
  ];

  return (
    <>
      <div className="w-full mx-auto p-4 md:p-6 space-y-4 md:space-y-6 bg-white rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Leave Requests </h3>
        </div>

        {/* Search + Filters */}
        <div className="w-full flex flex-col gap-3 mb-6">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1">
              <Input
                placeholder="Search by employee name"
                value={searchText}
                onChange={handleSearchChange}
                prefix={<SearchOutlined className="text-gray-500" />}
                className="px-4 py-3 rounded-lg h-10"
              />
            </div>
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              options={statusOptions}
              className="min-w-[120px]"
              placeholder="Status"
            />
            <RangePicker
              onChange={handleDateRangeChange}
              placeholder={['Start Date', 'End Date']}
              className="min-w-[240px]"
              format="YYYY-MM-DD"
            />
            {/* <Button
              onClick={() => {
                setSearchText("");
                setStatusFilter("all");
                setDateRange(null);
                setPagination(prev => ({ ...prev, current: 1 }));
                fetchData(1, pagination.pageSize, "", "all", null);
              }}
              className="text-gray-600 hover:text-gray-800"
            >
              Reset
            </Button> */}
          </div>
        </div>

        <Spin spinning={loading} size="large">
          <Table
            columns={requestColumns}
            dataSource={canView ? tableData : []}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              total: canView ? pagination.total : 0,
              pageSize: pagination.pageSize,
              current: pagination.current,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100'],
              position: ['bottomCenter']
            }}
            onChange={handleTableChange}
          />
        </Spin>
        {!canView && !loading && (
          <div className="text-center text-gray-500 py-4">You don't have permission to view leave requests.</div>
        )}
      </div>
      <ModalWrapper
        open={editModalOpen}
        onRequestClose={handleCloseEditModal}
        title="Edit Leave Request"
      >
        <LeaveRequest
          initialData={editData}
          onClose={handleCloseEditModal}
          onSuccess={() => {
            handleCloseEditModal();
            // Optionally refetch leave requests here
          }}
        />
      </ModalWrapper>
      <ModalWrapper
        open={viewModalOpen}
        onRequestClose={handleCloseViewModal}
        title="View Leave Request"
      >
        <LeaveRequest
          initialData={viewData}
          onClose={handleCloseViewModal}
          readOnly={true}
        />
      </ModalWrapper>
    </>
  );
};

export default Leave;
